// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { observer } from "mobx-react-lite";
import { FC } from "react";
import { Outlet } from "react-router-dom";

import {
  ResidentsContextProvider,
  useRootStore,
  useStateCodeFromSlug,
} from "~@jii/data";
import { PageHydratorWithErrorLogging } from "~@jii/layout";
import { withPresenterManager } from "~hydration-utils";

import { ResidentsHydratorPresenter } from "./ResidentsHydratorPresenter";

const ManagedComponent: FC<{
  presenter: ResidentsHydratorPresenter;
}> = observer(function ResidentsHydrator({ presenter }) {
  const { residentsStore, userProperties } = presenter;
  return (
    <ResidentsContextProvider value={{ residentsStore, userProperties }}>
      <Outlet />
    </ResidentsContextProvider>
  );
});

function usePresenter() {
  const rootStore = useRootStore();
  const stateCode = useStateCodeFromSlug();
  // not really expected in practice, just for type safety
  if (!stateCode) return null;
  return new ResidentsHydratorPresenter(rootStore, stateCode);
}

export const ResidentsHydrator = withPresenterManager({
  usePresenter,
  managerIsObserver: false,
  ManagedComponent,
  HydratorComponent: PageHydratorWithErrorLogging,
});
